import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { NotificationService } from '../../services/notification.service';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';
import { ProductCatalogItemVM, ProductPdvOptionIndexItemV2 } from '../produtos-v2.models';

type ImportMode = 'products' | 'options';
type EditorMode = 'upload' | 'manual';
type PreviewStatus = 'ready' | 'unchanged' | 'not-found' | 'ambiguous' | 'invalid' | 'overridden';
type ManualItemStatus = 'ready' | 'unchanged' | 'invalid';

interface PreviewItem {
  lineNumber: number;
  rawLine: string;
  mode: ImportMode;
  label: string;
  currentCode: string;
  nextCode: string;
  status: PreviewStatus;
  message: string;
  targetId?: number;
  targetKey?: string;
}

interface ManualItem {
  mode: ImportMode;
  targetId: number;
  label: string;
  subtitle: string;
  searchText: string;
  currentCode: string;
  nextCode: string;
  status: ManualItemStatus;
  message: string;
}

@Component({
  selector: 'app-importador-codigos-pdv-v2',
  templateUrl: './importador-codigos-pdv-v2.component.html',
  styleUrls: ['./importador-codigos-pdv-v2.component.scss']
})
export class ImportadorCodigosPdvV2Component implements OnInit, OnChanges {
  @Input() empresa: any;
  @Input() catalogItems: ProductCatalogItemVM[] = [];

  windowRef: DialogRef;
  mode: ImportMode = 'products';
  editorMode: EditorMode = 'upload';
  rawInput = '';
  previewItems: PreviewItem[] = [];
  manualItems: ManualItem[] = [];
  manualItemsLoaded = false;
  manualSearch = '';
  manualOnlyChanged = false;
  manualOnlyWithoutCode = false;
  loadingOptionIndex = false;
  applying = false;

  private optionIndex: ProductPdvOptionIndexItemV2[] = [];
  private optionIndexLoaded = false;
  private productsByName = new Map<string, ProductCatalogItemVM[]>();

  constructor(
    private facade: ProdutosV2FacadeService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.rebuildProductIndex();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalogItems']) {
      this.rebuildProductIndex();
    }
  }

  inicialize(windowRef: DialogRef): void {
    this.windowRef = windowRef;
  }

  get title(): string {
    return this.mode === 'products' ? 'Códigos PDV em lote para produtos' : 'Códigos PDV em lote para adicionais';
  }

  get subtitle(): string {
    if (this.usingManualEditor) {
      return this.mode === 'products'
        ? 'Carregue os produtos do catálogo, filtre a lista e altere os códigos PDV diretamente na tela.'
        : 'Carregue os adicionais, filtre por produto ou opção e ajuste os códigos PDV sem precisar subir planilha.';
    }

    return this.mode === 'products'
      ? 'Cole uma lista simples de produto e código. Também aceita arquivo .csv ou .txt.'
      : 'Cole produto, adicional, opção e código para atualizar várias opções de uma vez.';
  }

  get exampleLines(): string[] {
    if (this.mode === 'products') {
      return [
        'X-Burger Especial;12345',
        '#152;99881'
      ];
    }

    return [
      'X-Burger Especial;Molhos;Maionese verde;90012',
      '#152;Molhos;Cheddar cremoso;90013'
    ];
  }

  get readyCount(): number {
    return this.previewItems.filter((item) => item.status === 'ready').length;
  }

  get issueCount(): number {
    return this.previewItems.filter((item) => (
      item.status === 'not-found'
      || item.status === 'ambiguous'
      || item.status === 'invalid'
    )).length;
  }

  get unchangedCount(): number {
    return this.previewItems.filter((item) => item.status === 'unchanged').length;
  }

  get overriddenCount(): number {
    return this.previewItems.filter((item) => item.status === 'overridden').length;
  }

  get usingManualEditor(): boolean {
    return this.editorMode === 'manual';
  }

  get manualLoadedCount(): number {
    return this.manualItems.length;
  }

  get manualChangedCount(): number {
    return this.manualItems.filter((item) => this.hasManualChange(item)).length;
  }

  get manualReadyCount(): number {
    return this.manualItems.filter((item) => item.status === 'ready').length;
  }

  get manualInvalidCount(): number {
    return this.manualItems.filter((item) => item.status === 'invalid').length;
  }

  get canApply(): boolean {
    if (this.applying) return false;
    return this.usingManualEditor ? this.manualReadyCount > 0 : this.readyCount > 0;
  }

  get canDownloadTemplate(): boolean {
    if (this.mode === 'products') {
      return (this.catalogItems || []).length > 0;
    }

    return !!this.empresa?.catalogo?.id;
  }

  get downloadTemplateLabel(): string {
    return this.mode === 'products'
      ? 'Baixar planilha de produtos'
      : 'Baixar planilha de adicionais';
  }

  get manualLoadLabel(): string {
    if (this.manualItemsLoaded) {
      return this.mode === 'products' ? 'Recarregar produtos' : 'Recarregar adicionais';
    }

    if (this.mode === 'products') {
      const total = (this.catalogItems || []).length;
      return total ? `Carregar ${total} produto(s)` : 'Carregar produtos';
    }

    return 'Carregar adicionais';
  }

  get manualSearchPlaceholder(): string {
    return this.mode === 'products'
      ? 'Busque por produto, categoria, id ou código'
      : 'Busque por produto, adicional, opção, id ou código';
  }

  get manualEmptyActionLabel(): string {
    return this.mode === 'products' ? 'Carregar produtos' : 'Carregar adicionais';
  }

  get filteredManualItems(): ManualItem[] {
    let items = this.manualItems.slice();
    const search = this.normalizeToken(this.manualSearch);

    if (search) {
      items = items.filter((item) => this.normalizeToken([
        item.searchText,
        item.currentCode,
        item.nextCode
      ].join(' ')).indexOf(search) >= 0);
    }

    if (this.manualOnlyChanged) {
      items = items.filter((item) => this.hasManualChange(item));
    }

    if (this.manualOnlyWithoutCode) {
      items = items.filter((item) => !String(item.currentCode || '').trim());
    }

    return items;
  }

  close(): void {
    this.windowRef?.close();
  }

  async switchMode(mode: ImportMode): Promise<void> {
    if (this.mode === mode) return;
    this.mode = mode;
    this.previewItems = [];
    this.resetManualItems();
  }

  switchEditorMode(mode: EditorMode): void {
    if (this.editorMode === mode) return;
    this.editorMode = mode;
    if (mode === 'manual') {
      this.previewItems = [];
    }
  }

  async onFileSelected(event: any): Promise<void> {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      this.rawInput = String(reader.result || '');
      await this.buildPreview();
    };
    reader.readAsText(file);
    event.target.value = '';
  }

  async buildPreview(): Promise<void> {
    const lines = String(this.rawInput || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => !!line);

    if (!lines.length) {
      this.previewItems = [];
      return;
    }

    if (this.mode === 'options') {
      try {
        await this.ensureOptionIndex();
      } catch (error) {
        return;
      }
    }

    const preview = this.mode === 'products'
      ? this.buildProductPreview(lines)
      : this.buildOptionPreview(lines);

    this.markOverriddenItems(preview);
    this.previewItems = preview;
  }

  async loadManualItems(): Promise<void> {
    try {
      const items = this.mode === 'products'
        ? this.buildManualProductItems()
        : await this.buildManualOptionItems();

      if (!items.length) {
        this.notificationService.showError(
          this.mode === 'products'
            ? 'Não há produtos para carregar nesta edição manual.'
            : 'Não há adicionais para carregar nesta edição manual.'
        );
        return;
      }

      this.manualItems = items;
      this.manualItemsLoaded = true;
    } catch (error: any) {
      this.notificationService.showError(error?.message || 'Não foi possível carregar a edição manual.');
    }
  }

  updateManualCode(item: ManualItem, value: any): void {
    item.nextCode = String(value || '').trim();
    this.refreshManualItem(item);
  }

  async downloadTemplate(): Promise<void> {
    if (this.mode === 'products') {
      this.downloadProductsTemplate();
      return;
    }

    await this.downloadOptionsTemplate();
  }

  async apply(): Promise<void> {
    if (!this.canApply) return;

    this.applying = true;

    try {
      if (this.usingManualEditor) {
        await this.applyManualChanges();
      } else {
        await this.applyPreviewChanges();
      }
    } catch (error: any) {
      this.notificationService.showError(error?.message || 'Não foi possível salvar os códigos PDV em lote.');
    } finally {
      this.applying = false;
    }
  }

  statusLabel(status: PreviewStatus | ManualItemStatus): string {
    switch (status) {
      case 'ready':
        return 'Pronto';
      case 'unchanged':
        return 'Sem alteração';
      case 'not-found':
        return 'Não encontrado';
      case 'ambiguous':
        return 'Ambíguo';
      case 'invalid':
        return 'Inválido';
      case 'overridden':
        return 'Substituído';
      default:
        return status;
    }
  }

  statusClass(status: PreviewStatus | ManualItemStatus): string {
    return `is-${status}`;
  }

  private resetManualItems(): void {
    this.manualItems = [];
    this.manualItemsLoaded = false;
    this.manualSearch = '';
    this.manualOnlyChanged = false;
    this.manualOnlyWithoutCode = false;
  }

  private rebuildProductIndex(): void {
    this.productsByName.clear();

    (this.catalogItems || []).forEach((item) => {
      const key = this.normalizeToken(item.name);
      if (!key) return;

      const list = this.productsByName.get(key) || [];
      list.push(item);
      this.productsByName.set(key, list);
    });
  }

  private buildManualProductItems(): ManualItem[] {
    return (this.catalogItems || [])
      .filter((item) => item?.id != null)
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      .map((item) => {
        const categoryName = String(item.categoryName || item.category || '');
        const currentCode = String(item.raw?.codigoPdv || '').trim();
        const manualItem: ManualItem = {
          mode: 'products',
          targetId: Number(item.id),
          label: String(item.name || ''),
          subtitle: categoryName ? `${categoryName} / #${item.id}` : `#${item.id}`,
          searchText: this.normalizeToken([
            item.id,
            item.name,
            categoryName,
            currentCode
          ].join(' ')),
          currentCode: currentCode,
          nextCode: currentCode,
          status: 'unchanged',
          message: ''
        };

        this.refreshManualItem(manualItem);
        return manualItem;
      });
  }

  private async buildManualOptionItems(): Promise<ManualItem[]> {
    await this.ensureOptionIndex();

    const grouped = new Map<number, ProductPdvOptionIndexItemV2[]>();

    this.optionIndex.forEach((item) => {
      const current = grouped.get(item.id) || [];
      current.push(item);
      grouped.set(item.id, current);
    });

    return Array.from(grouped.values())
      .sort((a, b) => {
        const aKey = `${a[0]?.nomeAdicional || ''} ${a[0]?.nome || ''}`;
        const bKey = `${b[0]?.nomeAdicional || ''} ${b[0]?.nome || ''}`;
        return aKey.localeCompare(bKey);
      })
      .map((group) => {
        const first = group[0];
        const productNames = Array.from(new Set(
          group
            .map((item) => String(item.nomeProduto || '').trim())
            .filter((item) => !!item)
        )).sort((a, b) => a.localeCompare(b));
        const currentCode = String(first?.codigoPdv || '').trim();
        const subtitle = productNames.length > 1
          ? `Compartilhado em ${productNames.length} produtos / #${first.id}`
          : `${productNames[0] || 'Sem produto'} / #${first.id}`;
        const manualItem: ManualItem = {
          mode: 'options',
          targetId: Number(first.id),
          label: `${first.nomeAdicional} > ${first.nome}`,
          subtitle: subtitle,
          searchText: this.normalizeToken([
            first.id,
            first.nomeAdicional,
            first.nome,
            currentCode,
            ...productNames
          ].join(' ')),
          currentCode: currentCode,
          nextCode: currentCode,
          status: 'unchanged',
          message: ''
        };

        this.refreshManualItem(manualItem);
        return manualItem;
      });
  }

  private refreshManualItem(item: ManualItem): void {
    const currentCode = String(item.currentCode || '').trim();
    const nextCode = String(item.nextCode || '').trim();

    if (nextCode === currentCode) {
      item.status = 'unchanged';
      item.message = currentCode
        ? 'Sem alteração.'
        : 'Sem código informado ainda.';
      return;
    }

    if (!nextCode) {
      item.status = 'invalid';
      item.message = 'Informe um código PDV para salvar.';
      return;
    }

    item.status = 'ready';
    item.message = item.mode === 'products'
      ? 'Produto pronto para atualizar.'
      : 'Adicional pronto para atualizar.';
  }

  private hasManualChange(item: ManualItem): boolean {
    return String(item.currentCode || '').trim() !== String(item.nextCode || '').trim();
  }

  private downloadProductsTemplate(): void {
    if (!this.canDownloadTemplate) {
      this.notificationService.showError('Não há produtos para gerar a planilha.');
      return;
    }

    const lines = [
      ['referencia', 'produto', 'codigo_pdv'].join(';')
    ];

    (this.catalogItems || [])
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      .forEach((item) => {
        lines.push([
          this.toCsvValue(`#${item.id}`),
          this.toCsvValue(item.name),
          this.toCsvValue(item.raw?.codigoPdv || '')
        ].join(';'));
      });

    const companyLabel = this.normalizeFileName(this.empresa?.nome || this.empresa?.dominio || 'catalogo');

    this.downloadCsvFile(`codigos-pdv-produtos-${companyLabel}.csv`, lines);
  }

  private async downloadOptionsTemplate(): Promise<void> {
    try {
      await this.ensureOptionIndex();
    } catch (error) {
      return;
    }

    if (!this.optionIndex.length) {
      this.notificationService.showError('Não há adicionais para gerar a planilha.');
      return;
    }

    const lines = [
      ['referencia_produto', 'produto', 'referencia_adicional', 'adicional', 'referencia_opcao', 'opcao', 'codigo_pdv'].join(';')
    ];

    this.optionIndex
      .slice()
      .sort((a, b) => {
        const productSort = String(a.nomeProduto || '').localeCompare(String(b.nomeProduto || ''));
        if (productSort !== 0) return productSort;

        const groupSort = String(a.nomeAdicional || '').localeCompare(String(b.nomeAdicional || ''));
        if (groupSort !== 0) return groupSort;

        return String(a.nome || '').localeCompare(String(b.nome || ''));
      })
      .forEach((item) => {
        lines.push([
          this.toCsvValue(`#${item.idProduto}`),
          this.toCsvValue(item.nomeProduto),
          this.toCsvValue(`#${item.idAdicional}`),
          this.toCsvValue(item.nomeAdicional),
          this.toCsvValue(`#${item.id}`),
          this.toCsvValue(item.nome),
          this.toCsvValue(item.codigoPdv || '')
        ].join(';'));
      });

    const companyLabel = this.normalizeFileName(this.empresa?.nome || this.empresa?.dominio || 'catalogo');
    this.downloadCsvFile(`codigos-pdv-adicionais-${companyLabel}.csv`, lines);
  }

  private async applyPreviewChanges(): Promise<void> {
    const produtos: Array<{ id: number; codigoPdv: string }> = [];
    const opcoes: Array<{ id: number; codigoPdv: string }> = [];

    this.previewItems.forEach((item) => {
      if (item.status !== 'ready' || !item.targetId) return;

      if (item.mode === 'products') {
        produtos.push({ id: item.targetId, codigoPdv: item.nextCode });
      } else {
        opcoes.push({ id: item.targetId, codigoPdv: item.nextCode });
      }
    });

    const resposta = await this.facade.salveCodigosPdvLote(this.empresa?.catalogo, produtos, opcoes);
    const totalAtualizado = Number(resposta?.produtosAtualizados || 0) + Number(resposta?.opcoesAtualizadas || 0);

    this.notificationService.showSuccess(`${totalAtualizado} código(s) PDV atualizados em lote.`);
    this.windowRef?.close({ updated: true, totalAtualizado: totalAtualizado });
  }

  private async applyManualChanges(): Promise<void> {
    const produtos = this.manualItems
      .filter((item) => item.mode === 'products' && item.status === 'ready')
      .map((item) => ({ id: item.targetId, codigoPdv: item.nextCode }));
    const opcoes = this.manualItems
      .filter((item) => item.mode === 'options' && item.status === 'ready')
      .map((item) => ({ id: item.targetId, codigoPdv: item.nextCode }));

    const resposta = await this.facade.salveCodigosPdvLote(this.empresa?.catalogo, produtos, opcoes);
    const totalAtualizado = Number(resposta?.produtosAtualizados || 0) + Number(resposta?.opcoesAtualizadas || 0);

    this.notificationService.showSuccess(`${totalAtualizado} código(s) PDV atualizados em lote.`);
    this.windowRef?.close({ updated: true, totalAtualizado: totalAtualizado });
  }

  private async ensureOptionIndex(): Promise<void> {
    if (this.optionIndexLoaded || this.loadingOptionIndex) return;

    this.loadingOptionIndex = true;

    try {
      this.optionIndex = await this.facade.carregueIndiceCodigosPdv(this.empresa?.catalogo);
      this.optionIndexLoaded = true;
    } catch (error: any) {
      this.notificationService.showError(error?.message || 'Não foi possível carregar o índice de adicionais.');
      throw error;
    } finally {
      this.loadingOptionIndex = false;
    }
  }

  private buildProductPreview(lines: string[]): PreviewItem[] {
    const preview: PreviewItem[] = [];

    lines.forEach((line, index) => {
      const columns = this.splitColumns(line);

      if (!columns.some((column) => column !== '')) return;
      if (index === 0 && this.looksLikeProductHeader(columns)) return;

      const parsed = this.parseProductColumns(columns);
      if (!parsed) {
        preview.push({
          lineNumber: index + 1,
          rawLine: line,
          mode: 'products',
          label: '-',
          currentCode: '',
          nextCode: '',
          status: 'invalid',
          message: 'Use o formato produto;código.'
        });
        return;
      }

      const matches = this.findProducts(parsed.reference);
      if (!matches.length) {
        preview.push({
          lineNumber: index + 1,
          rawLine: line,
          mode: 'products',
          label: parsed.reference,
          currentCode: '',
          nextCode: parsed.code,
          status: 'not-found',
          message: 'Produto não encontrado.'
        });
        return;
      }

      if (matches.length > 1) {
        preview.push({
          lineNumber: index + 1,
          rawLine: line,
          mode: 'products',
          label: parsed.reference,
          currentCode: '',
          nextCode: parsed.code,
          status: 'ambiguous',
          message: 'Mais de um produto encontrado. Use #id para identificar.'
        });
        return;
      }

      const product = matches[0];
      const currentCode = String(product.raw?.codigoPdv || '').trim();
      const nextCode = String(parsed.code || '').trim();

      preview.push({
        lineNumber: index + 1,
        rawLine: line,
        mode: 'products',
        label: `${product.name}${product.categoryName ? ` (${product.categoryName})` : ''}`,
        currentCode: currentCode,
        nextCode: nextCode,
        status: !nextCode ? 'invalid' : currentCode === nextCode ? 'unchanged' : 'ready',
        message: !nextCode
          ? 'Código PDV vazio.'
          : currentCode === nextCode
            ? 'O código informado já está salvo.'
            : 'Produto pronto para atualizar.',
        targetId: Number(product.id),
        targetKey: `product:${product.id}`
      });
    });

    return preview;
  }

  private buildOptionPreview(lines: string[]): PreviewItem[] {
    const preview: PreviewItem[] = [];

    lines.forEach((line, index) => {
      const columns = this.splitColumns(line);

      if (!columns.some((column) => column !== '')) return;
      if (index === 0 && this.looksLikeOptionHeader(columns)) return;

      const parsed = this.parseOptionColumns(columns);
      if (!parsed) {
        preview.push({
          lineNumber: index + 1,
          rawLine: line,
          mode: 'options',
          label: '-',
          currentCode: '',
          nextCode: '',
          status: 'invalid',
          message: 'Use o formato produto;adicional;opção;código.'
        });
        return;
      }

      const matches = this.findOptions(parsed.product, parsed.group, parsed.option);
      if (!matches.length) {
        preview.push({
          lineNumber: index + 1,
          rawLine: line,
          mode: 'options',
          label: `${parsed.product} > ${parsed.group} > ${parsed.option}`,
          currentCode: '',
          nextCode: parsed.code,
          status: 'not-found',
          message: 'Opção de adicional não encontrada.'
        });
        return;
      }

      if (matches.length > 1) {
        preview.push({
          lineNumber: index + 1,
          rawLine: line,
          mode: 'options',
          label: `${parsed.product} > ${parsed.group} > ${parsed.option}`,
          currentCode: '',
          nextCode: parsed.code,
          status: 'ambiguous',
          message: 'Mais de uma opção encontrada. Use referências com #id quando necessário.'
        });
        return;
      }

      const option = matches[0];
      const currentCode = String(option.codigoPdv || '').trim();
      const nextCode = String(parsed.code || '').trim();

      preview.push({
        lineNumber: index + 1,
        rawLine: line,
        mode: 'options',
        label: `${option.nomeProduto} > ${option.nomeAdicional} > ${option.nome}`,
        currentCode: currentCode,
        nextCode: nextCode,
        status: !nextCode ? 'invalid' : currentCode === nextCode ? 'unchanged' : 'ready',
        message: !nextCode
          ? 'Código PDV vazio.'
          : currentCode === nextCode
            ? 'O código informado já está salvo.'
            : 'Opção pronta para atualizar.',
        targetId: Number(option.id),
        targetKey: `option:${option.id}`
      });
    });

    return preview;
  }

  private parseProductColumns(columns: string[]): { reference: string; code: string } | null {
    if (!columns.length || columns.length < 2) return null;

    const first = columns[0];
    const last = columns[columns.length - 1];
    const firstMatches = this.findProducts(first).length;
    const lastMatches = this.findProducts(last).length;

    if (lastMatches > 0 && firstMatches === 0) {
      return { reference: last, code: first };
    }

    return {
      reference: first,
      code: last
    };
  }

  private parseOptionColumns(columns: string[]): { product: string; group: string; option: string; code: string } | null {
    if (!columns.length || columns.length < 4) return null;

    if (columns.length >= 7) {
      return {
        product: columns[0] || columns[1],
        group: columns[2] || columns[3],
        option: columns[4] || columns[5],
        code: columns[columns.length - 1]
      };
    }

    const layoutA = {
      product: columns[0],
      group: columns[1],
      option: columns[2],
      code: columns[columns.length - 1]
    };
    const layoutB = {
      product: columns[1],
      group: columns[2],
      option: columns[3],
      code: columns[0]
    };

    const layoutAMatches = this.findOptions(layoutA.product, layoutA.group, layoutA.option).length;
    const layoutBMatches = this.findOptions(layoutB.product, layoutB.group, layoutB.option).length;

    if (layoutBMatches > 0 && layoutAMatches === 0) {
      return layoutB;
    }

    return layoutA;
  }

  private splitColumns(line: string): string[] {
    let separator = ';';

    if (line.indexOf('\t') >= 0) {
      separator = '\t';
    } else if (line.indexOf(';') < 0 && line.indexOf(',') >= 0) {
      separator = ',';
    }

    return line.split(separator).map((part) => String(part || '').trim());
  }

  private looksLikeProductHeader(columns: string[]): boolean {
    const normalized = columns.map((column) => this.normalizeToken(column));
    return normalized.length >= 2
      && normalized.some((column) => column.indexOf('produto') >= 0 || column.indexOf('referencia') >= 0)
      && normalized[normalized.length - 1].indexOf('codigo') >= 0;
  }

  private looksLikeOptionHeader(columns: string[]): boolean {
    const normalized = columns.map((column) => this.normalizeToken(column));
    return normalized.length >= 4
      && normalized.some((column) => column.indexOf('produto') >= 0)
      && normalized.some((column) => column.indexOf('adicional') >= 0)
      && normalized.some((column) => column.indexOf('opcao') >= 0)
      && normalized[normalized.length - 1].indexOf('codigo') >= 0;
  }

  private findProducts(reference: string): ProductCatalogItemVM[] {
    const token = this.normalizeToken(reference);
    if (!token) return [];

    const idToken = this.extractIdToken(reference);
    if (idToken) {
      return (this.catalogItems || []).filter((item) => String(item.id) === idToken);
    }

    return this.productsByName.get(token) || [];
  }

  private findOptions(productRef: string, groupRef: string, optionRef: string): ProductPdvOptionIndexItemV2[] {
    let items = this.optionIndex.slice();

    items = this.filterOptionIndexByField(items, 'product', productRef);
    if (!items.length) return [];

    items = this.filterOptionIndexByField(items, 'group', groupRef);
    if (!items.length) return [];

    return this.filterOptionIndexByField(items, 'option', optionRef);
  }

  private filterOptionIndexByField(
    items: ProductPdvOptionIndexItemV2[],
    field: 'product' | 'group' | 'option',
    reference: string
  ): ProductPdvOptionIndexItemV2[] {
    const idToken = this.extractIdToken(reference);

    if (idToken) {
      return items.filter((item) => {
        if (field === 'product') return String(item.idProduto) === idToken;
        if (field === 'group') return String(item.idAdicional) === idToken;
        return String(item.id) === idToken;
      });
    }

    const token = this.normalizeToken(reference);
    return items.filter((item) => {
      if (field === 'product') return this.normalizeToken(item.nomeProduto) === token;
      if (field === 'group') return this.normalizeToken(item.nomeAdicional) === token;
      return this.normalizeToken(item.nome) === token;
    });
  }

  private extractIdToken(reference: string): string | null {
    const raw = String(reference || '').trim();
    if (!raw) return null;

    if (raw.charAt(0) === '#') {
      return raw.substring(1).trim();
    }

    return /^\d+$/.test(raw) ? raw : null;
  }

  private normalizeToken(value: any): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private normalizeFileName(value: string): string {
    return this.normalizeToken(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'catalogo';
  }

  private toCsvValue(value: any): string {
    return String(value || '')
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/;/g, ',')
      .trim();
  }

  private downloadCsvFile(fileName: string, lines: string[]): void {
    const content = `\uFEFF${lines.join('\r\n')}`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private markOverriddenItems(items: PreviewItem[]): void {
    const lastIndexByKey = new Map<string, number>();

    items.forEach((item, index) => {
      if (!item.targetKey) return;
      if (item.status === 'ready' || item.status === 'unchanged') {
        lastIndexByKey.set(item.targetKey, index);
      }
    });

    items.forEach((item, index) => {
      if (!item.targetKey) return;
      const lastIndex = lastIndexByKey.get(item.targetKey);
      if (lastIndex == null || lastIndex === index) return;
      if (item.status !== 'ready' && item.status !== 'unchanged') return;

      item.status = 'overridden';
      item.message = 'Existe uma linha mais abaixo para este mesmo item. Apenas a ultima sera aplicada.';
    });
  }
}
